import * as Fraction from 'fraction.js';
import { find } from 'lodash';
import { IntlShape } from 'react-intl';
import { AUTO_ACCEPT_ODDS_CHANGE, SHOW_TOTAL_STAKES_TOGGLE } from '../data-types/betting/bettingEnums';
import {
  PendingBet,
  PendingBetParams,
  PendingTicket,
  SystemBetStakeDetails,
  SystemBetTypeExtended,
} from '../data-types/betting/bettingInterfaces';
import { AvailableLanguage } from '../data-types/international/internationalEnums';
import { offerStatuses, sportCategories, ticketStatuses } from '../data-types/sports/sportsConstants';
import { RACING_LABEL } from '../data-types/sports/sportsEnums';
import { EventItem, Outcome } from '../data-types/sports/sportsInterfaces';
import { trackSportradarAddToBeslip } from '../services/sportradarPixel';
import { addPendingBet, removePendingBet, updateBets } from '../state/actions/betActions';
import { getOfferState } from './betslip';

export const createId = (eventId: number, offerId: number, outcomeId: number, extraValue?: string): string => {
  if (extraValue) {
    return `${eventId}-${offerId}-${outcomeId}-${extraValue}`;
  }
  return `${eventId}-${offerId}-${outcomeId}`;
};

export const selectOutcome = (
  id: string,
  offerId: number,
  outcomeId: number,
  odds: number,
  extraValue: string | undefined,
  event: EventItem,
  selected: string[],
  intl: IntlShape,
  locale: AvailableLanguage | undefined,
): string[] => {
  if (odds === 1 || event.suspended) {
    return selected;
  }

  const addedSelect = [...selected];
  const index = addedSelect.indexOf(id);
  if (index !== -1) {
    addedSelect.splice(index, 1);
    removePendingBet({ offerId, outcomeId, extraValue, event });
  } else {
    const added = addPendingBet({ offerId, outcomeId, extraValue, event }, intl);

    if (added) {
      trackSportradarAddToBeslip(event, locale);
      addedSelect.push(id);
    }
  }

  return addedSelect;
};

export const updateSelectedBets = (pendingBets: PendingBet[], event: EventItem): string[] => {
  const selected: string[] = [];

  // add previously selected pending bets for when the page is navigated back to
  pendingBets.forEach(bet => {
    if (bet.event.id === event.id) {
      const id = createId(event.id, bet.offerId, bet.outcomeId, bet.extraValue);
      selected.push(id);
    }
  });

  return selected;
};

export const getBetEachWayValue = (bet: PendingBet) => `1/${bet.event.eachWayReduction} ${bet.event.eachWayPlaces}`;

export const getEWOfferName = (bet: PendingBet, intl: IntlShape) => {
  const { formatMessage } = intl;
  const { event } = bet;
  const { sportId } = event;

  switch (sportId) {
    case sportCategories.HORSES:
      return `${getBetEachWayValue(bet)} ${formatMessage({ id: 'common.places' }).toUpperCase()}`;
    case sportCategories.GREY_HOUNDS:
      const ew = find(bet.event.extraInfo, { label: RACING_LABEL.EW });
      if (!!ew) {
        return ew.value;
      }
      return '';
    default:
      return '';
  }
};

export const getPlaceOddsForEW = (outcome: Outcome, sportId: number, originalPlaceOdds?: number, originalEachWayOdds?: string) => {
  const fallbackPlaceOdds = 1;

  switch (sportId) {
    case sportCategories.HORSES:
      return originalPlaceOdds || outcome.placeOdds || fallbackPlaceOdds;
    case sportCategories.GREY_HOUNDS:
      const isOriginalNumber = !isNaN(Number(originalEachWayOdds));

      if (isOriginalNumber) {
        return Number(originalEachWayOdds);
      }

      const isNumber = !isNaN(Number(outcome.eachWayOdds));
      return isNumber ? Number(outcome.eachWayOdds) : fallbackPlaceOdds;
    default:
      return fallbackPlaceOdds;
  }
};

export const updateBet = (bet: PendingBet, params: PendingBetParams) => {
  updateBets([bet], params);
};

export const filterOutPlacedBets = (bets: PendingBet[], tickets: PendingTicket) => {
  return bets.filter(
    bet =>
      !bet.ticketId ||
      !tickets[bet.ticketId] ||
      (tickets[bet.ticketId].status !== ticketStatuses.ACCEPTED && tickets[bet.ticketId].status !== ticketStatuses.RETAINABLE),
  );
};

export const getSystemBetsGlobalAllEWStatus = (
  availableSystemBetTypes: SystemBetTypeExtended[],
  systemBetStakesDetails: SystemBetStakeDetails[],
) => {
  const numberOfAvailableSystemBetsPlusAcca = availableSystemBetTypes.length + 1;
  if (numberOfAvailableSystemBetsPlusAcca === systemBetStakesDetails.length) {
    return systemBetStakesDetails.reduce((acc, systemBet) => acc && systemBet.isEachWay, true);
  }

  return false;
};

export const shouldShowTotalStakesForSystemBets = (showTotalStakesToggleStatus: SHOW_TOTAL_STAKES_TOGGLE) =>
  showTotalStakesToggleStatus === SHOW_TOTAL_STAKES_TOGGLE.ON;

export const anyOddsChanged = (bets: PendingBet[], intl: IntlShape, forSystemBets: boolean) =>
  bets.reduce((acc, bet) => {
    const { oddsString, placeOdds, eachWayOdds } = getOfferState(bet, intl);

    const oddsChanged =
      acc || oddsString !== bet.originalOdds || placeOdds !== bet.originalPlaceOdds || eachWayOdds !== bet.originalEachWayOdds;

    return forSystemBets ? bet.selectedInMultiples && oddsChanged : oddsChanged;
  }, false);

export const anyBetsSuspendedOrUnavailable = (bets: PendingBet[], intl: IntlShape) =>
  bets.reduce((acc, bet) => {
    const { offerStatus, oddsString } = getOfferState(bet, intl);
    const isSuspended = !!bet.event.suspended || offerStatus === offerStatuses.BETTING_PAUSED;
    const unavailable = oddsString === '' || offerStatus === offerStatuses.DISABLED;

    return acc || isSuspended || unavailable;
  }, false);

export const canAutoAcceptNewOdds = (autoAccept: AUTO_ACCEPT_ODDS_CHANGE) => autoAccept === AUTO_ACCEPT_ODDS_CHANGE.YES;

export const getStakePerTicket = (totalStakeValue: number, numberOfTickets: number) => totalStakeValue / numberOfTickets;

export const getStakePerBetFromSystemBet = (
  inputStake: string,
  numberOfTickets: number,
  showTotalStakesToggleStatus: SHOW_TOTAL_STAKES_TOGGLE,
) => {
  const stakeValue = parseFloat(inputStake);

  return shouldShowTotalStakesForSystemBets(showTotalStakesToggleStatus) ? getStakePerTicket(stakeValue, numberOfTickets) : stakeValue;
};

export const getTotalStakeForSystemBet = (
  inputStake: string,
  numberOfTickets: number,
  showTotalStakesToggleStatus: SHOW_TOTAL_STAKES_TOGGLE,
  isEachWay: boolean,
) => {
  const stakeValue = parseFloat(inputStake);
  let totalStake = shouldShowTotalStakesForSystemBets(showTotalStakesToggleStatus) ? stakeValue : stakeValue * numberOfTickets;

  if (isEachWay) {
    totalStake = totalStake * 2;
  }

  return totalStake;
};

export const calculateFractionalOdds = (odds: number) => {
  const oddsFraction = Fraction(odds - 1).simplify(0.01);

  return `${oddsFraction.n}/${oddsFraction.d}`;
};

export const computeSingleBetStakeKey = (eventId: number, offerId: number, outcomeId: number) => `${eventId}-${offerId}-${outcomeId}`;
